import {
  Box,
  HStack,
  LinkBox,
  LinkOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowRight } from '@time-line/components/icons';
import { useImage } from 'lib/sanity';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type { SanityProduct } from 'types/cms/SanityProduct';

import { SLIDER_CONTAINER_BREAKPOINT } from '.';

type ProductLinkProps = {
  sanityProduct: SanityProduct;
  isPromotionPage: boolean;
};

export function ProductLink({
  sanityProduct,
  isPromotionPage,
}: ProductLinkProps) {
  const productImageProps = useImage(sanityProduct.images[0] ?? null, {
    ratio: 4 / 5,
  });

  return (
    <LinkBox
      position="relative"
      bottom={0}
      zIndex={2}
      w="full"
      p={{ base: '3xs', md: 'xs' }}
      pr="sm"
      bg="blackAlpha.400"
      backdropFilter="blur(50px)"
      borderTopRadius={0}
      borderBottomRadius="base"
      transitionDuration="fast"
      mx="auto"
      _hover={{
        bg: !isPromotionPage && 'blackAlpha.500',
      }}
      sx={{
        [`@container (min-width: ${SLIDER_CONTAINER_BREAKPOINT})`]: {
          position: 'absolute',
          bottom: 'sm',
          left: 0,
          right: 0,
          p: '4xs',
          pr: '2xs',
          borderTopRadius: 'xl',
          borderBottomRadius: 'xl',
          maxW: '280px',
        },
      }}
    >
      {!isPromotionPage && (
        <LinkOverlay
          as={NextLink}
          href={sanityProduct.href}
          aria-label={`Results from ${sanityProduct.title}`}
        />
      )}
      <HStack
        align="center"
        spacing="3xs"
        pointerEvents={isPromotionPage ? 'auto' : 'none'}
      >
        {productImageProps && (
          <Box
            borderRadius="base"
            overflow="hidden"
            width="46px"
            flexShrink={0}
          >
            <NextImage {...productImageProps} sizes="60px" />
          </Box>
        )}
        <VStack align="flex-start" spacing="0" flex={1}>
          <Text size="title3" color="whiteAlpha.700">
            Results from
          </Text>
          <Text
            size="title3"
            color="gray.50"
            noOfLines={2}
            textOverflow="ellipsis"
          >
            {sanityProduct.title}
          </Text>
        </VStack>
        {!isPromotionPage && (
          <ArrowRight boxSize="sm" color="gray.50" flexShrink={0} />
        )}
      </HStack>
    </LinkBox>
  );
}
